import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, finalize } from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import { buildDashboardEducationalPathsUrl, isDashboardShareLinkActive } from '../dashboard-share-link-api';
import { postDashboardListData } from '../dashboard-data-api';
import { ListResponseDTO, createEmptyListResponse } from '../../../dto/list-response.dto';
import { ListRequestDTO } from '../../../dto/list-request.dto';
import { ComponentDataSourceService } from '../../component-data-source/component-data-source.service';

// Cache mechanism
let cachedLocationProgressData: any = null;
let lastCacheTime = 0;
const CACHE_DURATION_MS = 30000; // 30 seconds cache
// Track in-flight requests by request key (parallel loads with different pages are allowed)
const inFlightLocationRequests = new Map<string, Observable<ListResponseDTO>>();
// Add flag to force real API usage for debugging
let forceRealApiData = true;

function locationProgressRequestKey(request: ListRequestDTO): string {
  return JSON.stringify({
    page: request.page ?? 1,
    pageSize: request.pageSize ?? 10,
    filters: request.filters ?? {},
    search: request.search ?? '',
    sortBy: request.sortBy,
    sortDir: request.sortDir,
    shareLink: isDashboardShareLinkActive(),
  });
}

function fetchLocationProgressOnce(
  componentDataSourceService: ComponentDataSourceService,
  request: ListRequestDTO
): Observable<ListResponseDTO> {
  if (isDashboardShareLinkActive()) {
    return postDashboardListData(componentDataSourceService.http, 'locationProgress', request).pipe(
      map((response) => mapLocationProgressListResponse(response, request)),
      catchError((error: HttpErrorResponse) => {
        console.error(`[LOCATION-PROGRESS-DATA] Share-link dashboard API error (${error.status}):`, error);
        if (forceRealApiData) {
          throw error;
        }
        return of(generateMockLocationListResponse(request.filters || {}));
      })
    );
  }

  const loadDataParams = {
    dataSourceKey: 'locations',
    filters: [
      { column: 'pageIndex', value: (request.page || 1) - 1, operator: '=' },
      { column: 'pageSize', value: request.pageSize || 1000, operator: '=' },
      ...Object.entries(request.filters || {}).flatMap(([key, value]) => {
        if (value === undefined || value === null || value === '' || value === 'all') {
          return [];
        }
        if (key === 'groupId') {
          return [{ column: 'group_id', value, operator: '=' as const }];
        }
        return [{ column: key, value, operator: '=' as const }];
      })
    ],
    searchQuery: request.search,
    sort: request.sortBy ? {
      active: request.sortBy,
      direction: request.sortDir || 'asc'
    } : undefined
  };

  return componentDataSourceService['loadDataWithStandardFiltersGET']('locations', '/locations/with-progress', loadDataParams).pipe(
    map((response: any) => {
      let locations: any[] = [];

      if (response && response.items && Array.isArray(response.items)) {
        locations = response.items.map((location: any) => {
          const transformedLocation = {
            id: location.guid || location.id || '',
            name: location.locations_name || location.name || 'Unknown Location',
            type: 'station',
            firefighterCount: location.firefighter_count || 0,
            okPercentage: location.progress_percentage || 0,
            registeredPercentage: location.registered_percentage || 0,
            ok_percentage: `${location.registered_percentage || 0}% (${location.progress_percentage || 0}%)`,
            registered_percentage: `${location.registered_percentage || 0}% (${location.progress_percentage || 0}%)`,
            userCount: location.firefighter_count || 0,
            averageProgress: location.registered_percentage || 0,
            completionRate: location.progress_percentage || 0,
            progress_percentage: location.progress_percentage || 0,
            assigned_percentage: location.registered_percentage || 0,
            status: (location.registered_percentage || 0) >= 80 ? 'ok' :
                   (location.registered_percentage || 0) >= 50 ? 'warning' : 'critical',
            totalUsers: location.firefighter_count || 0,
            completedUsers: Math.round((location.firefighter_count || 0) * (location.progress_percentage || 0) / 100),
            inProgressUsers: Math.round((location.firefighter_count || 0) * ((location.registered_percentage || 0) - (location.progress_percentage || 0)) / 100),
            notStartedUsers: Math.round((location.firefighter_count || 0) * (100 - (location.registered_percentage || 0)) / 100),
            educationalPaths: [],
            lastUpdated: new Date().toISOString(),
            cleanName: location.clean_name || '',
            data: location.data || {},
            rooms: location.rooms || [],
            users: location.users || []
          };

          return transformedLocation;
        });
      } else {
        console.warn(`[LOCATION-PROGRESS-DATA] No items found in API response or invalid response structure`);
        console.warn(`[LOCATION-PROGRESS-DATA] Response structure:`, response);
      }

      const listResponse: ListResponseDTO = {
        data: locations,
        meta: {
          page: response.pageIndex ? response.pageIndex + 1 : (request.page || 1),
          pageSize: response.pageSize || request.pageSize || locations.length,
          totalItems: response.totalCount || locations.length,
          totalPages: Math.max(
            1,
            Math.ceil((response.totalCount || locations.length) / (request.pageSize || 10))
          ),
          sortBy: request.sortBy,
          sortDir: request.sortDir
        },
        filters: response.filters || request.filters || {}
      };

      return listResponse;
    }),
    catchError((error: HttpErrorResponse) => {
      console.error(`[LOCATION-PROGRESS-DATA] *** API ERROR *** Locations API error (${error.status}): ${error.message}`);
      console.error(`[LOCATION-PROGRESS-DATA] *** API ERROR DETAILS ***`, error);

      if (forceRealApiData) {
        console.error(`[LOCATION-PROGRESS-DATA] *** FORCE REAL API DATA IS TRUE - THROWING ERROR ***`);
        throw error;
      }
      console.error(`[LOCATION-PROGRESS-DATA] *** FALLING BACK TO MOCK DATA ***`);
      return of(generateMockLocationListResponse(request.filters || {}));
    })
  );
}

/**
 * Get location progress data from the API
 * @param componentDataSourceService ComponentDataSourceService instance
 * @param request ListRequestDTO with pagination, sorting and filtering parameters
 * @returns Observable of location progress data as ListResponseDTO
 */
export function getLocationProgressData(
  componentDataSourceService: ComponentDataSourceService,
  request: ListRequestDTO
): Observable<ListResponseDTO> {

  cachedLocationProgressData = null;
  lastCacheTime = 0;

  const key = locationProgressRequestKey(request);
  const existing = inFlightLocationRequests.get(key);
  if (existing) {
    return existing;
  }

  const request$ = fetchLocationProgressOnce(componentDataSourceService, request).pipe(
    finalize(() => {
      inFlightLocationRequests.delete(key);
    }),
    shareReplay(1)
  );
  inFlightLocationRequests.set(key, request$);
  return request$;
}

/**
 * Get list of educational paths for filtering locations
 * @param componentDataSourceService ComponentDataSourceService instance
 * @returns Observable of educational paths
 */
function mapLocationProgressListResponse(response: ListResponseDTO, request: ListRequestDTO): ListResponseDTO {
  const rawItems = response.data || [];
  const locations = rawItems.map((location: any) => {
    const registered = location.registered_percentage ?? location.registeredPercentage ?? 0;
    const progress = location.progress_percentage ?? location.progressPercentage ?? 0;
    const registeredNum = typeof registered === 'string' ? parseFloat(registered) : registered;
    const progressNum = typeof progress === 'string' ? parseFloat(progress) : progress;
    return {
      id: location.guid || location.id || '',
      name: location.locations_name || location.name || 'Unknown Location',
      type: 'station',
      firefighterCount: location.firefighter_count || location.firefighterCount || 0,
      okPercentage: progressNum || 0,
      registeredPercentage: registeredNum || 0,
      ok_percentage: `${registeredNum || 0}% (${progressNum || 0}%)`,
      registered_percentage: `${registeredNum || 0}% (${progressNum || 0}%)`,
      userCount: location.firefighter_count || location.firefighterCount || 0,
      averageProgress: registeredNum || 0,
      completionRate: progressNum || 0,
      progress_percentage: progressNum || 0,
      assigned_percentage: registeredNum || 0,
      status: (registeredNum || 0) >= 80 ? 'ok' : (registeredNum || 0) >= 50 ? 'warning' : 'critical',
      totalUsers: location.firefighter_count || location.firefighterCount || 0,
      completedUsers: Math.round((location.firefighter_count || 0) * (progressNum || 0) / 100),
      inProgressUsers: Math.round((location.firefighter_count || 0) * ((registeredNum || 0) - (progressNum || 0)) / 100),
      notStartedUsers: Math.round((location.firefighter_count || 0) * (100 - (registeredNum || 0)) / 100),
      educationalPaths: location.educationalPaths || [],
      lastUpdated: new Date().toISOString(),
      cleanName: location.clean_name || location.cleanName || '',
      data: location.data || {},
      rooms: location.rooms || [],
      users: location.users || [],
    };
  });

  return {
    data: locations,
    meta: {
      page: response.meta?.page ?? request.page ?? 1,
      pageSize: response.meta?.pageSize ?? request.pageSize ?? locations.length,
      totalItems: response.meta?.totalItems ?? locations.length,
      totalPages: response.meta?.totalPages ?? 1,
      sortBy: request.sortBy,
      sortDir: request.sortDir,
    },
    filters: response.filters || request.filters || {},
  };
}

export function getEducationalPathsForLocations(componentDataSourceService: ComponentDataSourceService): Observable<any[]> {
  return componentDataSourceService.http.get<any[]>(buildDashboardEducationalPathsUrl()).pipe(
    catchError(() => of([
      { id: 'indsatsleder', name: 'Indsatsleder', description: 'Uddannelse for indsatsledere' },
      { id: 'holdleder', name: 'Holdleder', description: 'Uddannelse for holdledere' },
      { id: 'fui', name: 'FUI', description: 'Grundlæggende uddannelse for brandmænd' }
    ]))
  );
}

/**
 * Generate mock location progress data for testing and fallback
 * @param filters Optional filters to apply to the mock data
 */
function generateMockLocationListResponse(filters: any = {}): ListResponseDTO {
  
  // Get mock data from the existing function
  const mockData = generateMockLocationData(filters);
  
  // Create a ListResponseDTO
  const listResponse: ListResponseDTO = {
    data: mockData.locations || [],
    meta: {
      page: 1,
      pageSize: mockData.locations?.length || 0,
      totalItems: mockData.total || (mockData.locations?.length || 0),
      totalPages: 1,
      sortBy: filters.sortBy,
      sortDir: filters.sortDir
    },
    filters: filters
  };
  
  return listResponse;
}

/**
 * Generate mock location progress data for testing and fallback
 * @param filters Optional filters to apply to the mock data
 */
function generateMockLocationData(filters: any = {}): any {
  
  // Base mock data - always include some test locations
  let locations = [
    { 
      id: '1', 
      name: 'Station Nord', 
      type: 'station', // Add type field
      firefighterCount: 25, 
      okPercentage: 75, // Actual completion
      registeredPercentage: 85, // Assigned/registered 
      // Format percentages as strings with dual display
      ok_percentage: '85% (75%)', // registered% (ok%)
      registered_percentage: '85% (75%)', // registered% (ok%)
      userCount: 25, 
      averageProgress: 85, // Show registered as main progress
      completionRate: 75, // Actual completion rate
      progress_percentage: 75, // Actual completion
      assigned_percentage: 85, // What's assigned
      status: 'ok',
      totalUsers: 25,
      completedUsers: 19,
      inProgressUsers: 4,
      notStartedUsers: 2,
      educationalPaths: ['indsatsleder', 'fui'],
      lastUpdated: new Date().toISOString()
    },
    { 
      id: '2', 
      name: 'Station Syd', 
      type: 'station', // Add type field
      firefighterCount: 18, 
      okPercentage: 65, // Actual completion
      registeredPercentage: 72, // Assigned/registered
      // Format percentages as strings with dual display
      ok_percentage: '72% (65%)', // registered% (ok%)
      registered_percentage: '72% (65%)', // registered% (ok%)
      userCount: 18, 
      averageProgress: 72, // Show registered as main progress
      completionRate: 65, // Actual completion rate
      progress_percentage: 65, // Actual completion
      assigned_percentage: 72, // What's assigned
      status: 'warning',
      totalUsers: 18,
      completedUsers: 12,
      inProgressUsers: 4,
      notStartedUsers: 2,
      educationalPaths: ['holdleder', 'fui'],
      lastUpdated: new Date().toISOString()
    },
    { 
      id: '3', 
      name: 'Station Øst', 
      type: 'station', // Add type field
      firefighterCount: 22, 
      okPercentage: 88, // Actual completion
      registeredPercentage: 91, // Assigned/registered
      // Format percentages as strings with dual display
      ok_percentage: '91% (88%)', // registered% (ok%)
      registered_percentage: '91% (88%)', // registered% (ok%)
      userCount: 22, 
      averageProgress: 91, // Show registered as main progress
      completionRate: 88, // Actual completion rate
      progress_percentage: 88, // Actual completion
      assigned_percentage: 91, // What's assigned
      status: 'ok',
      totalUsers: 22,
      completedUsers: 19,
      inProgressUsers: 2,
      notStartedUsers: 1,
      educationalPaths: ['indsatsleder', 'holdleder'],
      lastUpdated: new Date().toISOString()
    },
    { 
      id: '4', 
      name: 'Station Vest', 
      type: 'station', // Add type field
      firefighterCount: 15, 
      okPercentage: 45, // Actual completion
      registeredPercentage: 58, // Assigned/registered
      // Format percentages as strings with dual display
      ok_percentage: '58% (45%)', // registered% (ok%)
      registered_percentage: '58% (45%)', // registered% (ok%)
      userCount: 15, 
      averageProgress: 58, // Show registered as main progress
      completionRate: 45, // Actual completion rate
      progress_percentage: 45, // Actual completion
      assigned_percentage: 58, // What's assigned
      status: 'warning',
      totalUsers: 15,
      completedUsers: 7,
      inProgressUsers: 5,
      notStartedUsers: 3,
      educationalPaths: ['fui'],
      lastUpdated: new Date().toISOString()
    },
    { 
      id: '5', 
      name: 'Havnen', 
      type: 'training', // Different type for variety
      firefighterCount: 12, 
      okPercentage: 32, // Actual completion
      registeredPercentage: 42, // Assigned/registered
      // Format percentages as strings with dual display
      ok_percentage: '42% (32%)', // registered% (ok%)
      registered_percentage: '42% (32%)', // registered% (ok%)
      userCount: 12, 
      averageProgress: 42, // Show registered as main progress
      completionRate: 32, // Actual completion rate
      progress_percentage: 32, // Actual completion
      assigned_percentage: 42, // What's assigned
      status: 'critical',
      totalUsers: 12,
      completedUsers: 4,
      inProgressUsers: 6,
      notStartedUsers: 2,
      educationalPaths: ['fui'],
      lastUpdated: new Date().toISOString()
    },
    { 
      id: '6', 
      name: 'Træningscenter', 
      type: 'training', // Different type for variety
      firefighterCount: 8, 
      okPercentage: 92, // Actual completion
      registeredPercentage: 95, // Assigned/registered
      // Format percentages as strings with dual display
      ok_percentage: '95% (92%)', // registered% (ok%)
      registered_percentage: '95% (92%)', // registered% (ok%)
      userCount: 8, 
      averageProgress: 95, // Show registered as main progress
      completionRate: 92, // Actual completion rate
      progress_percentage: 92, // Actual completion
      assigned_percentage: 95, // What's assigned
      status: 'ok',
      totalUsers: 8,
      completedUsers: 7,
      inProgressUsers: 1,
      notStartedUsers: 0,
      educationalPaths: ['indsatsleder', 'holdleder', 'fui'],
      lastUpdated: new Date().toISOString()
    }
  ];
  
  // Apply filtering like a backend would
  const result = filterCachedLocationData({ locations, total: locations.length }, filters);
  return result;
}

/**
 * Apply filters to cached location data to simulate backend filtering
 */
function filterCachedLocationData(data: any, filters: any = {}): any {
  
  if (!data || !data.locations) {
    return { locations: [], total: 0, filteredPath: '' };
  }
  
  let filteredLocations = [...data.locations];
  
  // Filter by educational path
  if (filters.educationalPathId && filters.educationalPathId !== 'all') {
    filteredLocations = filteredLocations.filter(location => 
      location.educationalPaths && location.educationalPaths.includes(filters.educationalPathId)
    );
  }
  
  // Filter by status
  if (filters.status && filters.status !== 'all') {
    if (filters.status === 'ok') {
      filteredLocations = filteredLocations.filter(location => location.status === 'ok');
    } else if (filters.status === 'warning') {
      filteredLocations = filteredLocations.filter(location => location.status === 'warning');
    } else if (filters.status === 'critical') {
      filteredLocations = filteredLocations.filter(location => location.status === 'critical');
    }
  }

  // Filter by progress percentage range
  if (filters.minProgress !== undefined) {
    filteredLocations = filteredLocations.filter(location => 
      location.averageProgress >= filters.minProgress);
  }
  
  if (filters.maxProgress !== undefined) {
    filteredLocations = filteredLocations.filter(location => 
      location.averageProgress <= filters.maxProgress);
  }
  
  // Filter by group
  if (filters.group) {
    if (filters.group === 'completed') {
      filteredLocations = filteredLocations.filter(location => location.averageProgress === 100);
    } else if (filters.group === 'in-progress') {
      filteredLocations = filteredLocations.filter(location => {
        return location.averageProgress > 0 && location.averageProgress < 100;
      });
    } else if (filters.group === 'not-started') {
      filteredLocations = filteredLocations.filter(location => location.averageProgress === 0);
    }
  }
  
  // Apply sorting
  if (filters.sortBy) {
    
    const direction = (filters.sortDir || filters.sortDirection) === 'desc' ? -1 : 1;
    
    filteredLocations.sort((a, b) => {
      // Handle sorting by name
      if (filters.sortBy === 'name') {
        return direction * (a.name || '').localeCompare(b.name || '');
      }
      
      // Handle sorting by progress fields
      if (filters.sortBy === 'averageProgress' || filters.sortBy === 'completionRate' || filters.sortBy === 'userCount' || filters.sortBy === 'registeredPercentage') {
        const aValue = a[filters.sortBy] || 0;
        const bValue = b[filters.sortBy] || 0;
        return direction * (aValue - bValue);
      }
      
      // Default sorting for other fields
      const aValue = a[filters.sortBy];
      const bValue = b[filters.sortBy];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return direction * aValue.localeCompare(bValue);
      }
      
      if (aValue < bValue) return -1 * direction;
      if (aValue > bValue) return 1 * direction;
      return 0;
    });
  }
  
  
  return {
    locations: filteredLocations,
    total: filteredLocations.length,
    filteredPath: filters.educationalPathId || '',
    filters: filters
  };
}

/**
 * Force reload location progress data by clearing cache and forcing real API usage
 * @param componentDataSourceService ComponentDataSourceService instance
 * @param request ListRequestDTO with pagination, sorting and filtering parameters
 * @returns Observable of location progress data as ListResponseDTO
 */
export function forceReloadLocationProgressData(
  componentDataSourceService: ComponentDataSourceService,
  request: ListRequestDTO
): Observable<ListResponseDTO> {
  
  // Clear cache and reset loading state
  cachedLocationProgressData = null;
  lastCacheTime = 0;
  inFlightLocationRequests.clear();

  forceRealApiData = true;
  
  // Call the main function
  return getLocationProgressData(componentDataSourceService, request);
} 